import React, { useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, Calendar, CalendarDays, CalendarRange } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportExporter = ({ type = 'dashboard' }) => {
  const [loading, setLoading] = useState(false);

  const getDateRange = (period) => {
    const now = new Date();
    let startDate, endDate;
    
    endDate = new Date(now);
    
    switch (period) {
      case 'weekly':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case 'monthly':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'yearly':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
    }
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const exportToExcel = (data, fileName, sheetName) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    // Auto-size columns
    const maxWidth = 50;
    const colWidths = Object.keys(data[0] || {}).map(key => ({
      wch: Math.min(maxWidth, Math.max(key.length, ...data.map(row => String(row[key] || '').length)))
    }));
    worksheet['!cols'] = colWidths;
    
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const generateDashboardReport = async (period) => {
    setLoading(true);
    try {
      const { start, end } = getDateRange(period);
      
      // Fetch all required data
      const [loansRes, customersRes, enquiriesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/loan-files`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/employees`)
      ]);

      // Filter by date range
      const filterByDate = (items, dateField = 'created_at') => {
        return items.filter(item => {
          const itemDate = new Date(item[dateField]);
          return itemDate >= new Date(start) && itemDate <= new Date(end);
        });
      };

      const loans = filterByDate(loansRes.data);
      const customers = filterByDate(customersRes.data);
      const enquiries = filterByDate(enquiriesRes.data);

      // Summary data
      const summaryData = [
        { 'Metric': 'Report Period', 'Value': `${formatDate(start)} to ${formatDate(end)}` },
        { 'Metric': 'Total Loan Applications', 'Value': loans.length },
        { 'Metric': 'Disbursed Loans', 'Value': loans.filter(l => l.stage === 'Disbursed').length },
        { 'Metric': 'Total Loan Amount', 'Value': `₹${loans.reduce((sum, l) => sum + (l.loan_amount || 0), 0).toLocaleString('en-IN')}` },
        { 'Metric': 'Disbursed Amount', 'Value': `₹${loans.filter(l => l.stage === 'Disbursed').reduce((sum, l) => sum + (l.loan_amount || 0), 0).toLocaleString('en-IN')}` },
        { 'Metric': 'New Customers', 'Value': customers.length },
        { 'Metric': 'Total Enquiries', 'Value': enquiries.length },
        { 'Metric': 'Converted Enquiries', 'Value': enquiries.filter(e => e.status === 'converted').length },
        { 'Metric': 'Total Employees', 'Value': employeesRes.data.length },
      ];

      // Loans by type
      const loansByType = {};
      loans.forEach(loan => {
        loansByType[loan.loan_type] = (loansByType[loan.loan_type] || 0) + 1;
      });
      const loanTypeData = Object.entries(loansByType).map(([type, count]) => ({
        'Loan Type': type,
        'Count': count
      }));

      // Loans by stage
      const loansByStage = {};
      loans.forEach(loan => {
        loansByStage[loan.stage] = (loansByStage[loan.stage] || 0) + 1;
      });
      const loanStageData = Object.entries(loansByStage).map(([stage, count]) => ({
        'Stage': stage,
        'Count': count
      }));

      // Create workbook with multiple sheets
      const workbook = XLSX.utils.book_new();
      
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryData), 'Summary');
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(loanTypeData), 'Loans by Type');
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(loanStageData), 'Loans by Stage');
      
      const fileName = `Dashboard_Report_${period}_${new Date().toISOString().split('T')[0]}`;
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      
      toast.success(`Dashboard ${period} report downloaded!`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const generateEnquiriesReport = async (period) => {
    setLoading(true);
    try {
      const { start, end } = getDateRange(period);
      const response = await axios.get(`${API}/inquiries`);
      
      const enquiries = response.data.filter(item => {
        const itemDate = new Date(item.created_at);
        return itemDate >= new Date(start) && itemDate <= new Date(end);
      });

      const reportData = enquiries.map(e => ({
        'Name': e.name,
        'Phone': e.phone,
        'Email': e.email || '-',
        'Loan Type': e.loan_type,
        'Message': e.message || '-',
        'Status': e.status?.charAt(0).toUpperCase() + e.status?.slice(1),
        'Date': formatDate(e.created_at)
      }));

      if (reportData.length === 0) {
        toast.warning('No enquiries found for this period');
        setLoading(false);
        return;
      }

      const fileName = `Enquiries_Report_${period}_${new Date().toISOString().split('T')[0]}`;
      exportToExcel(reportData, fileName, 'Enquiries');
      toast.success(`Enquiries ${period} report downloaded!`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const generateDisbursedLoansReport = async (period) => {
    setLoading(true);
    try {
      const { start, end } = getDateRange(period);
      const response = await axios.get(`${API}/loan-files`);
      
      // Filter disbursed loans by date
      const disbursedLoans = response.data.filter(loan => {
        if (loan.stage !== 'Disbursed') return false;
        const loanDate = new Date(loan.updated_at || loan.created_at);
        return loanDate >= new Date(start) && loanDate <= new Date(end);
      });

      const reportData = disbursedLoans.map(l => ({
        'File Number': l.file_number,
        'Customer Name': l.customer_name,
        'Loan Type': l.loan_type,
        'Loan Amount': `₹${(l.loan_amount || 0).toLocaleString('en-IN')}`,
        'Bank Agent': l.bank_agent_name || '-',
        'Assigned Employee': l.assigned_employee_name || '-',
        'Remarks': l.remarks || '-',
        'Created Date': formatDate(l.created_at),
        'Disbursed Date': formatDate(l.updated_at)
      }));

      if (reportData.length === 0) {
        toast.warning('No disbursed loans found for this period');
        setLoading(false);
        return;
      }

      // Summary
      const totalAmount = disbursedLoans.reduce((sum, l) => sum + (l.loan_amount || 0), 0);
      const summaryData = [
        { 'Metric': 'Report Period', 'Value': `${formatDate(start)} to ${formatDate(end)}` },
        { 'Metric': 'Total Disbursed Loans', 'Value': disbursedLoans.length },
        { 'Metric': 'Total Disbursed Amount', 'Value': `₹${totalAmount.toLocaleString('en-IN')}` },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryData), 'Summary');
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(reportData), 'Disbursed Loans');
      
      const fileName = `Disbursed_Loans_Report_${period}_${new Date().toISOString().split('T')[0]}`;
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      
      toast.success(`Disbursed loans ${period} report downloaded!`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const generateAllLoansReport = async (period) => {
    setLoading(true);
    try {
      const { start, end } = getDateRange(period);
      const response = await axios.get(`${API}/loan-files`);
      
      const loans = response.data.filter(loan => {
        const loanDate = new Date(loan.created_at);
        return loanDate >= new Date(start) && loanDate <= new Date(end);
      });

      const reportData = loans.map(l => ({
        'File Number': l.file_number,
        'Customer Name': l.customer_name,
        'Loan Type': l.loan_type,
        'Loan Amount': `₹${(l.loan_amount || 0).toLocaleString('en-IN')}`,
        'Stage': l.stage,
        'Bank Agent': l.bank_agent_name || '-',
        'Assigned Employee': l.assigned_employee_name || '-',
        'Remarks': l.remarks || '-',
        'Created Date': formatDate(l.created_at)
      }));

      if (reportData.length === 0) {
        toast.warning('No loans found for this period');
        setLoading(false);
        return;
      }

      const fileName = `All_Loans_Report_${period}_${new Date().toISOString().split('T')[0]}`;
      exportToExcel(reportData, fileName, 'All Loans');
      toast.success(`All loans ${period} report downloaded!`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (period) => {
    switch (type) {
      case 'dashboard':
        generateDashboardReport(period);
        break;
      case 'enquiries':
        generateEnquiriesReport(period);
        break;
      case 'disbursed':
        generateDisbursedLoansReport(period);
        break;
      case 'loans':
        generateAllLoansReport(period);
        break;
      default:
        generateDashboardReport(period);
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'dashboard': return 'Dashboard Report';
      case 'enquiries': return 'Enquiries Report';
      case 'disbursed': return 'Disbursed Loans';
      case 'loans': return 'All Loans Report';
      default: return 'Export Report';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white"
          disabled={loading}
          data-testid={`export-${type}-btn`}
        >
          {loading ? (
            <div className="animate-spin h-4 w-4 border-2 border-brand-navy border-t-transparent rounded-full mr-2" />
          ) : (
            <FileSpreadsheet className="h-4 w-4 mr-2" />
          )}
          Export Excel
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-xs text-slate-500">{getTitle()}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={() => handleExport('weekly')}
          className="cursor-pointer"
          data-testid={`export-${type}-weekly`}
        >
          <Calendar className="h-4 w-4 mr-2 text-blue-500" />
          Weekly Report
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleExport('monthly')}
          className="cursor-pointer"
          data-testid={`export-${type}-monthly`}
        >
          <CalendarDays className="h-4 w-4 mr-2 text-emerald-500" />
          Monthly Report
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleExport('yearly')}
          className="cursor-pointer"
          data-testid={`export-${type}-yearly`}
        >
          <CalendarRange className="h-4 w-4 mr-2 text-purple-500" />
          Yearly Report
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ReportExporter;
